<template>
  <div class="ui-accordion">
    <div
      v-for="(item, index) in items"
      :key="getItemKey(item, index)"
      class="ui-accordion-item"
      :class="{ 'is-open': isItemOpen(item, index) }"
    >
      <!-- Header -->
      <button
        class="ui-accordion-header"
        type="button"
        :aria-expanded="isItemOpen(item, index)"
        @click="toggleItem(item, index)"
      >
        <slot
          name="header"
          :item="item"
          :index="index"
          :isOpen="isItemOpen(item, index)"
        >
          <span class="ui-accordion-header-content">
            {{ typeof item === 'string' ? item : item.title || item.header }}
          </span>
        </slot>
        <span 
          class="ui-accordion-toggle-icon icon icon-chevron-down"
          :class="{ 'is-open': isItemOpen(item, index) }"
        ></span>
      </button>

      <!-- Content -->
      <transition name="accordion-collapse">
        <div 
          v-show="isItemOpen(item, index)" 
          class="ui-accordion-content"
        >
          <div class="ui-accordion-content-inner">
            <slot
              name="content"
              :item="item"
              :index="index"
              :isOpen="isItemOpen(item, index)"
            >
              <slot :name="`content-${getItemKey(item, index)}`">
                {{ typeof item === 'string' ? '' : item.content }}
              </slot>
            </slot>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
interface AccordionItem {
  title?: string;
  header?: string;
  content?: string;
  id?: string | number;
  [key: string]: any;
}

interface Props {
  items?: (AccordionItem | string)[];
  multiple?: boolean;
  openItems?: (string | number)[];
  itemKey?: string | ((item: AccordionItem | string, index: number) => string | number);
  defaultOpen?: (string | number)[];
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  multiple: true,
  openItems: () => [],
  itemKey: undefined,
  defaultOpen: () => []
});

const emit = defineEmits<{
  'update:openItems': [value: (string | number)[]];
  'item-toggle': [item: AccordionItem | string, index: number, isOpen: boolean];
}>();

const internalOpenItems = ref<(string | number)[]>([...props.defaultOpen]);

const currentOpenItems = computed({
  get: () => props.openItems.length > 0 ? props.openItems : internalOpenItems.value,
  set: (value: (string | number)[]) => {
    internalOpenItems.value = value;
    emit('update:openItems', value);
  }
});

const getItemKey = (item: AccordionItem | string, index: number): string | number => {
  if (typeof props.itemKey === 'function') {
    return props.itemKey(item, index);
  }
  if (typeof props.itemKey === 'string' && typeof item === 'object' && item !== null) {
    return item[props.itemKey] ?? index;
  }
  if (typeof item === 'object' && item !== null && 'id' in item) {
    return item.id ?? index;
  }
  return index;
};

const isItemOpen = (item: AccordionItem | string, index: number): boolean => {
  const key = getItemKey(item, index);
  return currentOpenItems.value.includes(key);
};

const toggleItem = (item: AccordionItem | string, index: number) => {
  const key = getItemKey(item, index);
  const isCurrentlyOpen = isItemOpen(item, index);
  
  let newOpenItems: (string | number)[];
  
  if (props.multiple) {
    if (isCurrentlyOpen) {
      newOpenItems = currentOpenItems.value.filter(openKey => openKey !== key);
    } else {
      newOpenItems = [...currentOpenItems.value, key];
    }
  } else {
    newOpenItems = isCurrentlyOpen ? [] : [key];
  }
  
  currentOpenItems.value = newOpenItems;
  emit('item-toggle', item, index, !isCurrentlyOpen);
};

// Expose public methods for programmatic control
defineExpose({
  openItem: (key: string | number) => {
    if (!currentOpenItems.value.includes(key)) {
      const newItems = props.multiple ? [...currentOpenItems.value, key] : [key];
      currentOpenItems.value = newItems;
    }
  },
  closeItem: (key: string | number) => {
    currentOpenItems.value = currentOpenItems.value.filter(openKey => openKey !== key);
  },
  toggleItem: (key: string | number) => {
    const itemIndex = props.items.findIndex((item, idx) => getItemKey(item, idx) === key);
    if (itemIndex >= 0 && props.items[itemIndex] !== undefined) {
      toggleItem(props.items[itemIndex], itemIndex);
    }
  },
  openAll: () => {
    if (props.multiple) {
      currentOpenItems.value = props.items.map((item, index) => getItemKey(item, index));
    }
  },
  closeAll: () => {
    currentOpenItems.value = [];
  }
});
</script>

<style lang="scss" scoped>
.ui-accordion {
  display: flex;
  flex-direction: column;
}

.ui-accordion-item {
  border-top: 1px solid #C6C6C6;

  &:first-child {
    border-top: none;
  }
}

.ui-accordion-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 20px 0;
  background: transparent;
  border: none;
  color: var(--black, #000);
  font-family: 'Albert Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  text-align: left;

  @media screen and (max-width: 768px) {
    padding: 26px 0;
  }

  &:hover {
    color: var(--black, #000);
  }

  &:focus-visible {
    outline: 2px solid var(--blue, #007bff);
    outline-offset: 2px;
  }
}

.ui-accordion-header-content {
  flex: 1;
  margin: 0;
}

.ui-accordion-toggle-icon {
  transition: transform 0.2s ease;
  font-size: 16px;
  color: currentColor;

  &.is-open {
    transform: rotate(180deg);
  }
}

.ui-accordion-content {
  overflow: hidden;
}

.ui-accordion-content-inner {
  padding-bottom: 20px;
}

/* Transition animations */
.accordion-collapse-enter-active,
.accordion-collapse-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.accordion-collapse-enter-from,
.accordion-collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

.accordion-collapse-enter-to,
.accordion-collapse-leave-from {
  max-height: 1000px;
  opacity: 1;
}

/* Variant classes for different use cases */
.ui-accordion {
  /* Checkout variant - matches the existing SpecialRequest styling */
  &.checkout-variant {
    .ui-accordion-item {
      border: none;
      border-radius: 8px;
      margin-bottom: 16px;
      background: var(--light-grey, #f8f9fa);

      &:last-child {
        margin-bottom: 0;
      }
    }

    .ui-accordion-header {
      padding: 16px;
      border-radius: 8px;
      background: transparent;
      border: none;
      
      .remarks-header {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        align-self: stretch;
        color: var(--black);
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: 24px;
        background: transparent;
        border: none;
        cursor: pointer;

        .icon {
          font-size: 16px;
        }
      }

      &:hover {
        background: transparent;
      }
    }

    .ui-accordion-toggle-icon {
      display: none; /* Hide default icon since remarks-header has its own */
    }

    .ui-accordion-content-inner {
      padding: 0;

      .remarks-body {
        padding: 16px 16px 0 16px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
        align-self: stretch;
      }
    }
  }

  /* Filter variant - matches the existing filter styling */
  &.filter-variant {
    border-top: 1px solid #C6C6C6;
    flex: 1;
    overflow-y: auto;

    .ui-accordion-item {
      border-top: 1px solid #C6C6C6;

      &:first-child {
        border-top: none;
      }

      &.is-open .ui-accordion-header {
        color: var(--black, #000);
      }
    }

    .ui-accordion-header {
      padding: 20px 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;

      @media screen and (max-width: 768px) {
        padding: 26px 0;
      }
    }

    .ui-accordion-content-inner {
      padding-bottom: 20px;

      &.amenities-filters-section {
        padding-bottom: 0;
      }
    }

    .ui-accordion-toggle-icon {
      display: none;
    }

    .filters-section__title {
      margin: 0;
      color: var(--black, #000);
      font-family: 'Albert Sans', sans-serif;
      font-weight: 500;
      font-size: 14px;
    }

    .filters-section__icon {
      transition: transform 0.2s ease;
      font-size: 16px;

      &.is-open {
        transform: rotate(180deg);
      }
    }
  }
}
</style>
